import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CodeIcon from '@mui/icons-material/Code';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlaceIcon from '@mui/icons-material/Place';
import { CmsProject, CmsSeoMetadata } from '../../types/cms';

const GOLD = '#9d6f32';
const BORDER = '#e8e8e8';

interface ProjectSeoTabProps {
  project: CmsProject;
  onChange: (updated: CmsProject) => void;
}

export const ProjectSeoTab: React.FC<ProjectSeoTabProps> = ({ project, onChange }) => {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const mainArea = project.microMarket || '';
  const surrounding = project.surroundingAreas && project.surroundingAreas.length > 0
    ? project.surroundingAreas
    : [];

  const seo = project.seo || {
    metaTitle: '',
    metaDescription: '',
    targetKeywords: [],
    canonicalUrl: `https://www.swarnagroup.in/projects/${project.slug}`,
    schemaType: 'RealEstateListing',
  };

  const updateSeo = (field: keyof CmsSeoMetadata, val: any) => {
    onChange({
      ...project,
      seo: { ...seo, [field]: val },
    });
  };

  const handleAddKeyword = (kw: string) => {
    if (!kw.trim()) return;
    const current = seo.targetKeywords || [];
    if (!current.includes(kw.trim().toLowerCase())) {
      updateSeo('targetKeywords', [...current, kw.trim().toLowerCase()]);
    }
  };

  const handleRemoveKeyword = (kw: string) => {
    updateSeo('targetKeywords', (seo.targetKeywords || []).filter((k) => k !== kw));
  };

  const handleGenerateAiSeo = () => {
    setIsOptimizing(true);
    setSuccessMessage(null);

    const configTypes = project.configurations?.map((c) => c.type).filter(Boolean) || [];
    const location = [mainArea, project.city].filter(Boolean).join(', ');
    const aiTitle = [project.name, configTypes.slice(0, 2).join(' & '), location].filter(Boolean).join(' | ');
    const aiDesc = [
      project.name && `Explore ${project.name}`,
      location && `in ${location}`,
      configTypes.length > 0 && `with ${configTypes.join(' and ')} homes`,
      project.reraNumber && `RERA: ${project.reraNumber}`,
    ].filter(Boolean).join(' ') + '.';
    const aiKeywords = location && configTypes.length > 0
      ? configTypes.map((type) => `${type.toLowerCase()} in ${location.toLowerCase()}`)
      : [];

    const updatedProject: CmsProject = {
      ...project,
      seo: {
        ...seo,
        metaTitle: aiTitle,
        metaDescription: aiDesc,
        targetKeywords: aiKeywords,
        canonicalUrl: `https://www.swarnagroup.in/projects/${project.slug}`,
        schemaType: seo.schemaType || 'RealEstateListing',
      },
    };

    onChange(updatedProject);
    setIsOptimizing(false);
    setSuccessMessage('Draft SEO text created from project fields. Check every claim before publishing.');
  };

  // Structured Data (JSON-LD) Preview
  const schemaJson = {
    '@context': 'https://schema.org',
    '@type': seo.schemaType || 'RealEstateListing',
    'name': project.name,
    'description': seo.metaDescription || project.shortDescription || project.description,
    'url': seo.canonicalUrl || `https://www.swarnagroup.in/projects/${project.slug}`,
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': project.location,
      'addressLocality': project.city || 'Surat',
      'addressRegion': 'Gujarat',
      'addressCountry': 'IN',
    },
    ...(mainArea ? { 'containedInPlace': { '@type': 'Place', 'name': mainArea } } : {}),
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
      {/* Top Banner Notice */}
      {successMessage && (
        <Alert
          severity="success"
          icon={<CheckCircleIcon fontSize="inherit" />}
          onClose={() => setSuccessMessage(null)}
          sx={{ borderRadius: 2 }}
        >
          {successMessage}
        </Alert>
      )}

      {/* 1. Google SERP Simulator */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GoogleIcon sx={{ color: '#4285F4', fontSize: 22 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              Search result preview (appearance may vary)
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={isOptimizing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleGenerateAiSeo}
              disabled={isOptimizing}
              sx={{
                bgcolor: GOLD,
                '&:hover': { bgcolor: '#805926' },
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              {isOptimizing ? 'Creating draft...' : 'Draft from project details'}
            </Button>
          </Stack>
        </Box>

        {/* Live Snippet Box */}
        <Box sx={{ p: 2.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', maxWidth: 650 }}>
          <Typography sx={{ fontSize: 12, color: '#202124', mb: 0.5 }}>
            https://www.swarnagroup.in › projects › <span style={{ color: '#5f6368' }}>{project.slug}</span>
          </Typography>
          <Typography sx={{ fontSize: 18, color: '#1a0dab', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            {seo.metaTitle || 'Add a page title'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#4d5156', mt: 0.5, lineHeight: 1.4 }}>
            {seo.metaDescription || 'Add a page description'}
          </Typography>
        </Box>
      </Paper>

      {/* 2. SEO Fields Editor */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <PlaceIcon sx={{ color: GOLD, fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            Page title, description, and search topics
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#6b6b6b', mb: 2.5 }}>
          Main location: <strong>{mainArea || 'Not entered'}</strong>{surrounding.length > 0 ? `; nearby areas: ${surrounding.join(', ')}` : ''}.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <TextField
              label="Page title"
              fullWidth
              size="small"
              value={seo.metaTitle}
              onChange={(e) => updateSeo('metaTitle', e.target.value)}
              helperText="Describe this specific project clearly. Google may rewrite the displayed title."
            />
          </Box>

          <Box>
            <TextField
              label="Page description"
              fullWidth
              multiline
              rows={3}
              size="small"
              value={seo.metaDescription}
              onChange={(e) => updateSeo('metaDescription', e.target.value)}
              helperText="Summarize verified details that buyers will find on the page. Google may choose a different snippet."
            />
          </Box>

          <TextField
            label="Canonical URL"
            fullWidth
            size="small"
            value={seo.canonicalUrl}
            onChange={(e) => updateSeo('canonicalUrl', e.target.value)}
          />

          {/* Keywords input */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#1a1a1a' }}>
              Target Search Queries & Micro-Markets
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              {(seo.targetKeywords || []).map((kw) => (
                <Chip
                  key={kw}
                  label={kw}
                  size="small"
                  onDelete={() => handleRemoveKeyword(kw)}
                  sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 500 }}
                />
              ))}
            </Box>
            <TextField
              placeholder="Type search keyword (e.g. 2 bhk flats in vanakla surat) and press Enter"
              size="small"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddKeyword((e.target as HTMLInputElement).value);
                  (e.target as HTMLInputElement).value = '';
                }
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* 3. JSON-LD RealEstateListing Structured Data Preview */}
      <Paper sx={{ p: 3, borderRadius: 2, border: `1px solid ${BORDER}`, boxShadow: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon sx={{ color: GOLD }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              Structured Data Schema (JSON-LD)
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Schema Type</InputLabel>
            <Select
              label="Schema Type"
              value={seo.schemaType || 'RealEstateListing'}
              onChange={(e) => updateSeo('schemaType', e.target.value)}
            >
              <MenuItem value="RealEstateListing">RealEstateListing (Recommended)</MenuItem>
              <MenuItem value="ApartmentComplex">ApartmentComplex</MenuItem>
              <MenuItem value="HousingComplex">HousingComplex</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Typography variant="body2" sx={{ color: '#6b6b6b', mb: 2 }}>
          This is a draft preview. Structured data must match visible, verified project details and does not guarantee a search feature or ranking.
        </Typography>

        <Box
          component="pre"
          sx={{
            p: 2,
            bgcolor: '#1e1e1e',
            color: '#4ade80',
            borderRadius: 1.5,
            fontSize: 12,
            overflowX: 'auto',
            fontFamily: 'monospace',
            maxHeight: 280,
          }}
        >
          {JSON.stringify(schemaJson, null, 2)}
        </Box>
      </Paper>
    </Box>
  );
};
